import { ContextMenu, ContextMenuItem, ContextMenuModel } from "./custom-context-menu";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { TimesheetShortcutsSet } from "./timesheet-shortcuts-set";
import { Txt } from "../common/text";
import { C } from "../common/component";
import { ModalBuilder } from "../common/modal-builder";
import { TimesheetSelectedCells } from "./timesheet-right-table-configurer";
import { getSelectedCells } from "./timesheet-right-table";

interface CellCoords {
    row: number;
    col: number;
}

type ContextMenuPayload = CellCoords

export class HotContextMenuAdapter
{
    private handler?: (e: MouseEvent) => void;
    private menu?: ContextMenu;
    private readonly targets: HTMLElement[] = [];
    private lastCoords: CellCoords | null = null;
    private clipboardGrid: string[][] | null = null;
    private savedSelection: number[] | null = null;

    constructor(private readonly hot: Handsontable,
        private readonly commentsManager: TimesheetCommentsManager,
        private readonly dataManager: TimesheetRightTableDataManager,
        private readonly shortcuts: TimesheetShortcutsSet,
        private readonly customButtons: ContextMenuItem[] = [])
    {}

    attach(el: HTMLElement): void
    {
        if (!this.handler)
        {
            this.handler = (e) => this.onContextMenu(e);
        }
        el.addEventListener("contextmenu", this.handler, true);
        this.targets.push(el);
    }

    attachToHot(hot: Handsontable): void
    {
        const anyHot = hot as any;
        const container: HTMLElement = anyHot.container ?? anyHot.rootElement;

        const selectors = [
            container,
            ".ht_master",
            ".ht_clone_top",
            ".ht_clone_left",
            ".ht_clone_corner"
        ];

        selectors.forEach((selector) =>
        {
            const element = typeof selector === "string"
                ? container.querySelector<HTMLElement>(selector)
                : selector;

            if (element)
            {
                this.attach(element);
            }
        });
    }

    destroy(): void
    {
        if (this.handler)
        {
            this.targets.forEach((target) => target.removeEventListener("contextmenu", this.handler, true));
        }
        this.menu?.destroy();
        this.targets.length = 0;
        this.clipboardGrid = null;
        this.lastCoords = null;
    }

    private onContextMenu(e: MouseEvent): void
    {
        e.preventDefault();
        e.stopPropagation();

        const td = (e.target as HTMLElement).closest("td");
        if (!td)
        {
            return;
        }

        const coords = this.getCellCoords(td);
        if (!coords)
        {
            return;
        }

        this.lastCoords = coords;

        this.savedSelection = this.hot.getSelectedLast() ?? null;

        this.menu?.destroy();
        this.menu = new ContextMenu(this.buildModel());

        this.menu.open({
            anchorEvent: e,
            target: td,
            payload: coords
        });
    }

    private getCellCoords(td: HTMLElement): CellCoords | null
    {
        const coords = this.hot.getCoords(td) as any;

        if (!coords || coords.row < 0 || coords.col < 0)
        {
            return null;
        }

        return { row: coords.row, col: coords.col };
    }

    private buildModel(): ContextMenuModel
    {
        return {
            items: [
                this.createCopyItem(),
                this.createPasteItem(),
                this.createEditCommentItem(),
                this.createChangeRowCommentItem(),
                ...this.wrapCustomButtons(),
                ...this.createShortcutsItems()
            ]
        };
    }

    private wrapCustomButtons(): ContextMenuItem[]
    {
        return this.customButtons.map((button) => ({
            ...button,
            onClick: async (ctx) =>
            {
                this.ensureSelectionAtLastCoords();
                await button.onClick?.(ctx);
            }
        }));
    }

    private createCopyItem(): ContextMenuItem
    {
        return {
            text: () => Txt.copy,
            disabled: () => !this.hasValidSelection(),
            onClick: async () =>
            {
                const coords = this.lastCoords;
                const savedSel = this.savedSelection;

                if (savedSel && coords && this.isCoordInsideSelection(coords.row, coords.col, savedSel))
                {
                    const [r1, c1, r2, c2] = savedSel;
                    this.hot.selectCell(r1, c1, r2, c2);
                }
                else if (coords)
                {
                    this.selectCell(coords.row, coords.col);
                }

                const grid = copySelectionAsGrid(this.hot);

                if (!grid)
                {
                    return;
                }

                this.clipboardGrid = grid;
                await this.writeToClipboard(grid);

                this.menu?.destroy();
            }
        };
    }

    private async writeToClipboard(grid: string[][]): Promise<void>
    {
        try
        {
            const text = grid.map((row) => row.join("\t")).join("\n");
            await navigator.clipboard.writeText(text);
        }
        catch (error)
        {
            // Silently fail if clipboard access is denied
            console.debug("Clipboard write failed:", error);
        }
    }

    private createPasteItem(): ContextMenuItem
    {
        return {
            text: () => Txt.paste,
            disabled: () => !this.clipboardGrid,
            onClick: () =>
            {
                this.ensureSelectionAtLastCoords();

                if (!this.clipboardGrid)
                {
                    return;
                }

                this.pasteClipboardData();
            }
        };
    }

    private pasteClipboardData(): void
    {
        const sel = this.hot.getSelectedLast();
        if (!sel)
        {
            return;
        }

        const [row, col] = sel;
        const changes: [number, number, string][] = [];

        this.clipboardGrid.forEach((rowData, rowIndex) =>
        {
            rowData.forEach((value, colIndex) =>
            {
                changes.push([row + rowIndex, col + colIndex, value]);
            });
        });

        if (changes.length > 0)
        {
            this.hot.setDataAtCell(changes);
        }
    }

    private createEditCommentItem(): ContextMenuItem
    {
        return {
            text: () => Txt.addComment,
            hidden: () => !this.isSingleCellSelected(),
            disabled: () => !this.isCommentEditableForCurrentCell(),
            onClick: () =>
            {
                this.ensureSelectionAtLastCoords();
                this.openCommentEditor();
            }
        };
    }

    private isSingleCellSelected(): boolean
    {
        const coords = this.hot.getSelectedLast();
        const sel = getSelectedCells(coords);

        if (sel)
        {
            return sel.isSingleCell();
        }

        return !!this.lastCoords;
    }

    private isCommentEditableForCurrentCell(): boolean
    {
        const row = this.getCurrentRow();
        if (row == null)
        {
            return false;
        }
        return this.commentsManager.isCommentEditable(row);
    }

    private getCurrentRow(): number | null
    {
        return this.lastCoords?.row ?? this.hot.getSelectedLast()?.[0] ?? null;
    }

    private openCommentEditor(): void
    {
        const coords = this.lastCoords;
        if (!coords)
        {
            return;
        }

        const cell = this.hot.getCell(coords.row, coords.col);
        if (!(cell instanceof HTMLElement))
        {
            return;
        }

        const cellElement = C.create(cell);

        setTimeout(() =>
        {
            this.commentsManager.openNewComment(
                cellElement,
                coords.row,
                coords.col
            );
        }, 0);
    }

    private createChangeRowCommentItem(): ContextMenuItem
    {
        return {
            text: () => Txt.changeRowComment,
            disabled: () => !this.isCommentEditableForCurrentCell(),
            onClick: () =>
            {
                this.ensureSelectionAtLastCoords();
                this.openRowCommentModal();
            }
        };
    }

    private openRowCommentModal(): void
    {
        const row = this.getCurrentRow();
        if (row == null)
        {
            return;
        }

        const modal = ModalBuilder.createModal(Txt.comment);
        modal.body.addClass("change-password");

        const currentComment = this.commentsManager.getComment(row, null);

        const commentInput = C.input
            .addClass("form-control")
            .attr("type", "text")
            .attr("placeholder", Txt.comment)
            .val(currentComment)
            .appendTo(modal.body);

        const saveButton = C.button
            .addClass("btn btn-primary main-button")
            .text(Txt.save)
            .appendTo(modal.body);

        saveButton.click(async () =>
        {
            await this.saveRowComment(row, commentInput, modal);
        });

        modal.container.modal({
            backdrop: "static",
            keyboard: true,
            show: true
        });
    }

    private async saveRowComment(
        row: number,
        commentInput: any,
        modal: any
    ): Promise<void>
    {
        const commentValue = String(commentInput.val());

        await this.commentsManager.saveComment(row, null, commentValue);

        const rowHeaderFn = (rowNumber: number): string =>
            this.dataManager.getRowHeader(rowNumber, this.commentsManager);

        this.hot.updateSettings(
            { rowHeaders: rowHeaderFn as unknown as any },
            false
        );

        ModalBuilder.removeModal(modal.container);
    }

    private createShortcutsItems(): ContextMenuItem[]
    {
        return this.shortcuts.getDisplayGroups().map((group) => ({
            id: group.type,
            text: () => group.displayName,
            submenu: this.createShortcutSubmenuItems(group)
        }));
    }

    private createShortcutSubmenuItems(group: any): ContextMenuItem[]
    {
        return group.displayedShortcuts.map((sc: any) => ({
            id: `${group.type}:${sc.Abbreviation}`,
            text: () => this.formatShortcutName(sc),
            onClick: (ctx: any) => this.applyShortcut(ctx, sc)
        }));
    }

    private formatShortcutName(shortcut: any): string
    {
        return shortcut.Abbreviation
            ? `${shortcut.Name} (${shortcut.Abbreviation})`
            : shortcut.Name;
    }

    private applyShortcut(ctx: any, shortcut: any): void
    {
        const payload = ctx.payload as ContextMenuPayload | undefined;
        const row = payload?.row ?? this.lastCoords?.row;
        const col = payload?.col ?? this.lastCoords?.col;

        if (row == null || col == null)
        {
            return;
        }

        if (shortcut.Abbreviation)
        {
            this.hot.setDataAtCell(row, col, shortcut.Abbreviation);
        }
    }

    private hasValidSelection(): boolean
    {
        return !!this.lastCoords || !!this.hot.getSelectedLast();
    }

    private ensureSelectionAtLastCoords(): void
    {
        if (!this.lastCoords)
        {
            return;
        }

        const sel = this.hot.getSelectedLast();
        const { row, col } = this.lastCoords;

        if (this.isCoordInsideSelection(row, col, sel))
        {
            return; // Cell already selected, skip for performance
        }

        this.selectCell(row, col);
    }

    private isCoordInsideSelection(
        row: number,
        col: number,
        selection: number[] | undefined
    ): boolean
    {
        if (!selection)
        {
            return false;
        }

        const [r1, c1, r2, c2] = selection;

        return (
            row >= Math.min(r1, r2) &&
            row <= Math.max(r1, r2) &&
            col >= Math.min(c1, c2) &&
            col <= Math.max(c1, c2)
        );
    }

    private selectCell(row: number, col: number): void
    {
        try
        {
            // Try the extended API with scroll parameters
            (this.hot as any).selectCell(row, col, row, col, true, true, true);
        }
        catch
        {
            // Fallback to basic API
            (this.hot as any).selectCell(row, col);
        }

        this.hot.listen();
    }
}

function copySelectionAsGrid(hot: Handsontable): string[][] | null
{
    const selections = hot.getSelected();

    if (!selections || selections.length === 0)
    {
        return null;
    }

    const [r1, c1, r2, c2] = selections[selections.length - 1];

    const minR = Math.min(r1, r2);
    const maxR = Math.max(r1, r2);
    const minC = Math.min(c1, c2);
    const maxC = Math.max(c1, c2);

    const grid: string[][] = [];

    for (let r = minR; r <= maxR; r++)
    {
        const row: string[] = [];
        for (let c = minC; c <= maxC; c++)
        {
            const v = hot.getDataAtCell(r, c);
            row.push(v == null ? "" : String(v));
        }
        grid.push(row);
    }

    return grid;
}
